/**
 * Clipboard helpers for the "New Connection from Clipboard" command palette
 * action and its preview row.
 *
 * Privacy: the clipboard is only read when explicitly requested (palette open /
 * command execute) — never a background listener. The raw text is never shown,
 * stored or sent to analytics: the palette row is built only from the parsed,
 * masked fields — the engine and a safe target, the same source as the paste
 * preview card (C3 review r6, C3-23) — or a generic label.
 */

import { parseConnectionString } from './parseConnectionString';
import { engineDisplayLabel, safeConnectionTarget } from '@/types/safeConnectionDisplay';

/** The palette row for connection-string text that names no engine it can fill (it asks which, or it is refused). */
export const CLIPBOARD_CONNECTION_STRING_LABEL = 'Connection string on clipboard';

/** The clipboard text as the paste places read it: line breaks dropped (as a text input drops them), trimmed. */
export function clipboardConnectionText(raw: string | null | undefined): string {
  return (raw ?? '').replace(/[\r\n]+/gu, '').trim();
}

/** Truncate a preview string to ~`max` chars with an ellipsis. */
export function truncatePreview(s: string, max = 40): string {
  const trimmed = s.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1).trimEnd()}…`;
}

/** Text shaped like a connection string (`= ; @ : / \`) — the same test the start view uses before its failure line. */
const SHAPED = /[=;@:/\\]/u;

/**
 * The palette row for a clipboard string: "<Engine> · <target>" from the parse (the target through
 * `safeConnectionTarget`, as every displayed target is), the generic label for connection-string text it cannot fill, or
 * null for anything else. Never a piece of the raw text.
 */
export function buildClipboardPreview(raw: string | null | undefined): string | null {
  const text = clipboardConnectionText(raw);
  if (!text) return null;
  const result = parseConnectionString(text);
  if (result.engine && !result.problem) {
    const values = result.values;
    const target = safeConnectionTarget({
      engine: result.engine,
      server: typeof values.server === 'string' ? values.server : undefined,
      port: typeof values.port === 'string' && values.port !== '<auto>' ? values.port : undefined,
      oracleConnectionStringType: values.oracleConnectionStringType,
      oracleTnsAlias: values.oracleTnsAlias,
      oracleTnsDescriptor: values.oracleTnsDescriptor,
    });
    return truncatePreview(target ? `${engineDisplayLabel(result.engine)} · ${target}` : engineDisplayLabel(result.engine));
  }
  return result.engine || result.choices || result.refusal || SHAPED.test(text) ? CLIPBOARD_CONNECTION_STRING_LABEL : null;
}

/** Read clipboard text, preferring the async navigator API, falling back to Electron. */
export async function readClipboardText(): Promise<string> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.readText) {
      const text = await navigator.clipboard.readText();
      if (typeof text === 'string') return text;
    }
  } catch {
    // Fall through to the Electron bridge.
  }
  try {
    const text = window.electronAPI?.clipboard?.readText?.();
    return typeof text === 'string' ? text : '';
  } catch {
    return '';
  }
}

// ---------------------------------------------------------------------------
// Palette-row preview cache. The palette reads the clipboard once when it opens
// and stashes the masked preview here so the command's `rightHint()` can return
// it synchronously during render.
// ---------------------------------------------------------------------------

let cachedPreview: string | null = null;

export function setCachedClipboardPreview(value: string | null): void {
  cachedPreview = value;
}

export function getCachedClipboardPreview(): string | null {
  return cachedPreview;
}

/** Read + parse + mask the clipboard for the palette preview row. */
export async function readClipboardConnectionPreview(): Promise<string | null> {
  const raw = await readClipboardText();
  return buildClipboardPreview(raw);
}
